import { useEffect, useRef } from 'react';
import { useDebounce } from './useDebounce';
import { usePendingQuantitySave } from './usePendingQuantitySave';

export type QuantityCommitOutcome = 'success' | 'error';

/**
 * Debounces a quantity edit, tracks it against the shared pending-save counter
 * (see {@link usePendingQuantitySave}), and tells the caller whether a commit's
 * result is still current — an in-flight commit whose value has since been
 * superseded by a newer edit, a cancel, or a replacement must not drive a UI
 * revert/settle.
 *
 * `replacePending` swaps any queued or in-flight commit for another save (such as a
 * bundle-size change) that must hold the pending-save counter until it settles.
 */
export function useDebouncedQuantityCommit<TValue>(
  commit: (value: TValue) => Promise<void>,
  onSettled: (
    value: TValue,
    outcome: QuantityCommitOutcome,
    isStale: boolean,
    error?: unknown
  ) => void,
  delayMs: number,
  options?: { flushOnUnmount?: boolean }
) {
  const { begin, end } = usePendingQuantitySave();
  // Own sequence for staleness, kept independent of the pending-save token: begin()'s
  // return value is only a token to hand back to end(), not guaranteed unique per call.
  const sequenceRef = useRef(0);
  const pendingSaveVersionRef = useRef<number | null>(null);
  const isMountedRef = useRef(true);
  useEffect(() => {
    // Must reset here, not just at useRef's init: StrictMode's dev mount/cleanup/remount
    // cycle runs the cleanup below once, which would otherwise strand this at false forever.
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const endPendingSave = (pendingSaveVersion: number) => {
    end(pendingSaveVersion);
    if (pendingSaveVersionRef.current === pendingSaveVersion) {
      pendingSaveVersionRef.current = null;
    }
  };

  const debouncedCommit = useDebounce(
    async (value: TValue, pendingSaveVersion: number, sequence: number) => {
      try {
        await commit(value);
        if (isMountedRef.current) {
          onSettled(value, 'success', sequenceRef.current !== sequence);
        }
      } catch (error) {
        if (isMountedRef.current) {
          onSettled(value, 'error', sequenceRef.current !== sequence, error);
        }
      } finally {
        endPendingSave(pendingSaveVersion);
      }
    },
    delayMs,
    options
  );

  const trigger = (value: TValue) => {
    const pendingSaveVersion = begin();
    pendingSaveVersionRef.current = pendingSaveVersion;
    sequenceRef.current += 1;
    debouncedCommit(value, pendingSaveVersion, sequenceRef.current);
  };

  const cancelPending = () => {
    debouncedCommit.cancel();
    sequenceRef.current += 1;
    if (pendingSaveVersionRef.current !== null) {
      endPendingSave(pendingSaveVersionRef.current);
    }
  };

  const replacePending = async (
    replacement: (isStale: () => boolean) => Promise<void>
  ) => {
    debouncedCommit.cancel();
    sequenceRef.current += 1;
    const sequence = sequenceRef.current;
    const pendingSaveVersion = begin();
    pendingSaveVersionRef.current = pendingSaveVersion;
    try {
      await replacement(() => sequenceRef.current !== sequence);
    } finally {
      endPendingSave(pendingSaveVersion);
    }
  };

  return { trigger, cancelPending, replacePending };
}
